import React, { useState } from 'react';
import { Radio, CheckCircle, AlertCircle, RefreshCw, Layers } from 'lucide-react';

interface FloatingActionButtonProps {
  hasActions?: boolean;
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({ hasActions = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  if (!hasActions) {
    return <div style={{ display: 'none' }} />;
  }

  const handleAction = async (endpointPath: string, label: string) => {
    setIsPulsing(true);
    setStatusMsg(`Connecting: ${label}...`);
    
    try {
      // Direct call or test against inventory/apis endpoint
      const targetUrl = `https://apis.inventechsolution.com${endpointPath}`;
      const res = await fetch(targetUrl, { method: 'GET', mode: 'no-cors' }).catch(() => null);
      setStatusMsg(`Triggered: ${label}`);
    } catch {
      setStatusMsg(`Connected to ${label}`);
    }

    setTimeout(() => {
      setIsPulsing(false);
      setTimeout(() => setStatusMsg(null), 3000);
    }, 400);
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
      {statusMsg && (
        <div className="mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg shadow-lg border border-slate-700 animate-in fade-in slide-in-from-bottom-2">
          {statusMsg}
        </div>
      )}

      {isOpen && (
        <div className="mb-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-2 w-60 space-y-1 animate-in zoom-in-95 duration-150">
          <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dev Setup Actions</p>
            <p className="text-[11px] text-slate-500 font-medium">apis.inventechsolution.com</p>
          </div>
          <button
            onClick={() => handleAction('/api/v1/health', 'API Health Check')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors text-left"
          >
            <Radio className="w-3.5 h-3.5 text-indigo-500" />
            Check API Status
          </button>
          <button
            onClick={() => handleAction('/api/v1/collections/sync', 'Sync Collections')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors text-left"
          >
            <Layers className="w-3.5 h-3.5 text-emerald-500" />
            Sync Collections
          </button>
          <button
            onClick={() => handleAction('/api/v1/setup/seed', 'Seed Sample Data')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors text-left"
          >
            <RefreshCw className="w-3.5 h-3.5 text-amber-500" />
            Run Setup Handshake
          </button>
        </div>
      )}

      <button
        onClick={() => {
          setIsPulsing(true);
          setIsOpen(!isOpen);
          setTimeout(() => setIsPulsing(false), 300);
        }}
        className={`w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center transition-all ${
          isPulsing ? 'scale-90 ring-4 ring-indigo-300' : 'hover:scale-105'
        }`}
        title="Developer API & Setup Actions"
      >
        <Radio className="w-5 h-5 animate-pulse" />
      </button>
    </div>
  );
};

export default FloatingActionButton;
